import type { AccessPayload, SessionResult, TeacherSessionDraft, TeacherStudentDraft } from '../types'

interface ApiResponse {
  ok: boolean
  message?: string
  token?: string
  results?: unknown[]
  students?: unknown[]
  sessions?: unknown[]
  access?: AccessPayload
  skipped?: boolean
}

async function parseResponse(response: Response): Promise<ApiResponse> {
  try { return await response.json() as ApiResponse } catch { return { ok: false, message: 'El servidor entregó una respuesta inesperada.' } }
}

const DEMO_ACCESS: AccessPayload = {
  student: {
    id: 'DEMO-01', name: 'Estudiante de prueba', firstName: 'Estudiante', lastName: 'de prueba', course: '6°B',
    message: 'Lee cada pregunta con calma y confía en lo que aprendiste.',
    support: { profileId: 'estandar', shortPrompts: false, visualSupport: true, specificHints: true, reducedDistractions: false },
  },
  session: {
    id: 'U3-S01', title: 'Repaso progresivo de la Unidad 3', grade: '6° básico', unit: 'Unidad 3',
    selectedOAs: ['OA 07', 'OA 08', 'OA 09'], topics: [], difficulty: 'Progresiva', vocabularyLevel: 'Habitual', supportIntensity: 'Estándar', questionCount: 15,
    orderMode: 'random', completionAction: 'support_only', supportMaterialId: '',
    notebookActivity: 'Escribe un resumen con cuatro ideas importantes, dos conceptos históricos y una relación de causa y consecuencia.',
    learningGoal: 'Explicar cambios sociales, políticos y culturales del Chile del siglo XX.',
    historicalSkill: 'Analizar, comparar y reconocer cambios y permanencias.', materials: 'Cuaderno y lápiz.', active: true,
  },
  pendingSessions: 1,
}

export async function validateAccess(code: string, honeypot = ''): Promise<AccessPayload> {
  if (import.meta.env.DEV && code.trim() === '4827') return DEMO_ACCESS
  const response = await fetch('/api/validate-access', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code, website: honeypot }),
  })
  const data = await parseResponse(response)
  if (!response.ok || !data.ok || !data.access) throw new Error(data.message ?? 'No fue posible validar el código.')
  return data.access
}

export async function submitResult(result: SessionResult): Promise<void> {
  const response = await fetch('/api/submit-result', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(result),
  })
  const data = await parseResponse(response)
  if (!response.ok || !data.ok) throw new Error(data.message ?? 'No fue posible enviar el resultado.')
}

export async function teacherLogin(password: string): Promise<string> {
  const response = await fetch('/api/teacher-login', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }),
  })
  const data = await parseResponse(response)
  if (!response.ok || !data.ok || !data.token) throw new Error(data.message ?? 'No fue posible iniciar la sesión.')
  return data.token
}

export async function fetchTeacherResults(token: string): Promise<Record<string, unknown>[]> {
  const response = await fetch('/api/teacher-results', { headers: { Authorization: `Bearer ${token}` } })
  const data = await parseResponse(response)
  if (!response.ok || !data.ok) throw new Error(data.message ?? 'No fue posible cargar los resultados.')
  return (data.results ?? []) as Record<string, unknown>[]
}

export async function fetchTeacherConfig(token: string): Promise<{ students: Record<string, unknown>[]; sessions: Record<string, unknown>[] }> {
  const response = await fetch('/api/teacher-config', { headers: { Authorization: `Bearer ${token}` } })
  const data = await parseResponse(response)
  if (!response.ok || !data.ok) throw new Error(data.message ?? 'No fue posible cargar la configuración.')
  return { students: (data.students ?? []) as Record<string, unknown>[], sessions: (data.sessions ?? []) as Record<string, unknown>[] }
}

export async function saveContentCsv(token: string, fileName: string, csvText: string, initializeOnly = false): Promise<{ skipped: boolean }> {
  const response = await fetch('/api/teacher-save-content', {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ fileName, csvText, initializeOnly }),
  })
  const data = await parseResponse(response)
  if (!response.ok || !data.ok) throw new Error(data.message ?? 'No fue posible guardar el archivo.')
  return { skipped: Boolean(data.skipped) }
}

export async function saveTeacherItem(token: string, kind: 'student' | 'session', item: TeacherStudentDraft | TeacherSessionDraft): Promise<void> {
  const response = await fetch('/api/teacher-save-config', {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ operation: 'save', kind, item }),
  })
  const data = await parseResponse(response)
  if (!response.ok || !data.ok) throw new Error(data.message ?? 'No fue posible guardar los cambios.')
}

export async function deleteTeacherItem(token: string, kind: 'student' | 'session', id: string): Promise<void> {
  const response = await fetch('/api/teacher-save-config', {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ operation: 'delete', kind, id }),
  })
  const data = await parseResponse(response)
  if (!response.ok || !data.ok) throw new Error(data.message ?? 'No fue posible eliminar el elemento.')
}
